import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Box, Button, TextField } from '@mui/material';
import ChipInput from '../components/ChipInput';

const normalizeAssociated = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  return [];
};

const DermoPlaintForm = ({ plaint, onClose, onSave }) => {
  const initialData = useMemo(
    () => ({
      plaint: '',
      associatedPlaintes: [],
    }),
    []
  );

  const [formData, setFormData] = useState(initialData);
  const [saving, setSaving] = useState(false);
  const associatedInputRef = useRef(null);

  useEffect(() => {
    if (!plaint) {
      setFormData(initialData);
      return;
    }

    setFormData({
      plaint: plaint.plaint || '',
      associatedPlaintes: normalizeAssociated(plaint.associatedPlaintes),
    });
  }, [plaint, initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddAssociated = (value) => {
    setFormData((prev) => {
      const exists = prev.associatedPlaintes.some(
        (x) => String(x).toLowerCase() === String(value).toLowerCase()
      );
      if (exists) return prev;
      return { ...prev, associatedPlaintes: [...prev.associatedPlaintes, value] };
    });
  };

  const handleDeleteAssociated = (index) => {
    setFormData((prev) => ({
      ...prev,
      associatedPlaintes: prev.associatedPlaintes.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const headers = {
        'x-access-token': localStorage.getItem('token') || sessionStorage.getItem('token'),
        Authorization: `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`,
      };

      const pendingAssociated = associatedInputRef.current?.value?.trim();
      const associatedPlaintes = pendingAssociated
        ? Array.from(
            new Set([
              ...(formData.associatedPlaintes || []),
              pendingAssociated,
            ].map((v) => String(v).trim()).filter(Boolean))
          )
        : (formData.associatedPlaintes || []);

      const payload = {
        plaint: formData.plaint.trim(),
        associatedPlaintes,
      };

      if (plaint && plaint._id) {
        await axios.put(`/complaints/${plaint._id}`, payload, { headers });
      } else {
        await axios.post('/complaints', payload, { headers });
      }

      if (associatedInputRef.current) associatedInputRef.current.value = '';

      onSave();
      onClose();
    } catch (error) {
      console.error(error);
      alert(error?.response?.data?.message || 'Failed to save dermo plaint');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ py: 1 }}>
        <TextField
          label="Plaint"
          name="plaint"
          value={formData.plaint}
          onChange={handleChange}
          fullWidth
          margin="normal"
          required
        />

        <ChipInput
          label="Associated plaintes"
          values={formData.associatedPlaintes}
          onAdd={handleAddAssociated}
          onDelete={handleDeleteAssociated}
          inputRef={associatedInputRef}
        />

        <Box mt={2}>
          <Button type="submit" variant="contained" color="primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Box>
  );
};

export default DermoPlaintForm;
